import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PROGRAMS } from "@/data/content";
import { tone } from "@/lib/tones";

const CARDS = PROGRAMS.slice(0, 3);
const LAYOUT = [
  { z: 0, x: 0, y: 0, r: -7 },
  { z: 30, x: 52, y: -34, r: -2 },
  { z: 60, x: 104, y: -68, r: 3 },
];

export const useCanHover = () => {
  const [ok, setOk] = useState(false);
  useEffect(() => { setOk(window.matchMedia?.("(hover: hover) and (pointer: fine)").matches ?? false); }, []);
  return ok;
};

export const ProgramStack = ({ onSelect }) => {
  const ref = useRef(null);
  const canHover = useCanHover();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 110, damping: 16 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 110, damping: 16 });

  const onMove = (e) => {
    if (!canHover) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <div
      ref={ref}
      data-testid="program-stack"
      onMouseMove={onMove}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
      className="stack-scene relative h-[225px] w-[330px] sm:h-[235px] sm:w-[350px]"
    >
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative h-full w-full">
        {CARDS.map((c, i) => {
          const t = tone(c.tone);
          const L = LAYOUT[i];
          return (
            <motion.div
              key={c.name}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 + i * 0.14, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              style={{ zIndex: i + 1, transformStyle: "preserve-3d" }}
              className="absolute bottom-0 left-0"
            >
              <button
                type="button"
                data-testid={`program-card-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => onSelect?.(c)}
                style={{ transform: `translate3d(${L.x}px, ${L.y}px, ${L.z}px) rotate(${L.r}deg)` }}
                className="stack-card relative flex h-[150px] w-[220px] flex-col justify-between rounded-2xl border border-white/60 bg-white/95 p-4 text-left backdrop-blur-md sm:h-[160px] sm:w-[240px]"
              >
                <div className="flex items-start justify-between">
                  <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${t.solid}`}>{c.tag}</span>
                  <ArrowUpRight className="h-4 w-4 text-forest/40" />
                </div>
                <div>
                  <p className="font-display text-2xl font-semibold leading-none text-forest">{c.name}</p>
                  <p className="mt-1.5 text-[11px] leading-snug text-dim">{c.detail}</p>
                </div>
                <span className={`absolute inset-x-4 bottom-0 h-[3px] rounded-t-full ${t.dot}`} />
              </button>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
};
